import { createContext, useContext } from "react";
import type * as React from "react";

import { SidebarIcon } from "./Icons";
import { cn } from "../lib/cn";

const CollapsedContext = createContext(false);

const FOCUS =
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-lite";

export interface NavRailProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  "children"
> {
  wordmark: React.ReactNode;
  /** Shown in place of the wordmark while collapsed; the toggle reveals itself over it on hover. */
  mark?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  ref?: React.Ref<HTMLElement>;
}

export function NavRail({
  wordmark,
  mark,
  children,
  footer,
  collapsed = false,
  onToggleCollapsed,
  className,
  ...rest
}: NavRailProps) {
  const markOnly = collapsed && mark != null;

  return (
    <nav
      aria-label="Workstation"
      {...rest}
      className={cn(
        "flex h-full shrink-0 flex-col gap-1 bg-nav text-ink-on-nav",
        collapsed ? "w-[68px]" : "w-[232px]",
        className,
      )}
    >
      <div
        className={cn(
          "flex items-center px-[18px] pt-[22px] pb-[18px]",
          collapsed ? "justify-center" : "justify-between gap-3",
        )}
      >
        {!collapsed && <div className="min-w-0">{wordmark}</div>}
        {markOnly && !onToggleCollapsed && mark}
        {onToggleCollapsed && (
          <button
            type="button"
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            aria-expanded={!collapsed}
            title={collapsed ? "Expand navigation" : "Collapse navigation"}
            onClick={onToggleCollapsed}
            className={cn(
              "group inline-flex size-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md border text-ink-on-nav-muted",
              "transition duration-150 ease-paper hover:bg-ink-on-nav/10 hover:text-ink-on-nav",
              markOnly
                ? "border-transparent hover:border-nav-rule focus-visible:border-nav-rule"
                : "border-nav-rule",
              FOCUS,
            )}
          >
            {markOnly && (
              <span
                aria-hidden="true"
                className="inline-flex group-hover:hidden group-focus-visible:hidden"
              >
                {mark}
              </span>
            )}
            <SidebarIcon
              strokeWidth={1.8}
              className={cn(
                markOnly &&
                  "hidden group-hover:inline-block group-focus-visible:inline-block",
              )}
            />
          </button>
        )}
      </div>
      <CollapsedContext value={collapsed}>
        {children}
        <div className="flex-1" />
        {footer != null && (
          <div className="border-t border-nav-rule pt-2.5 pb-3.5">{footer}</div>
        )}
      </CollapsedContext>
    </nav>
  );
}

export interface NavItemProps extends Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "children"
> {
  icon: React.ReactNode;
  label: string;
  count?: number;
  active?: boolean;
  ref?: React.Ref<HTMLAnchorElement>;
}

export function NavItem({
  icon,
  label,
  count,
  active = false,
  className,
  ...rest
}: NavItemProps) {
  const collapsed = useContext(CollapsedContext);
  const badge = count != null && count > 0;

  return (
    <a
      aria-current={active ? "page" : undefined}
      aria-label={badge ? `${label}, ${count}` : label}
      title={collapsed ? label : undefined}
      {...rest}
      className={cn(
        "relative mx-2.5 flex items-center gap-3 rounded-lg py-3 no-underline",
        "transition duration-150 ease-paper",
        collapsed ? "justify-center px-0" : "px-[18px]",
        active
          ? "bg-nav-raised text-ink-on-nav"
          : "text-ink-on-nav-muted hover:bg-ink-on-nav/8 hover:text-ink-on-nav",
        FOCUS,
        className,
      )}
    >
      <span aria-hidden="true" className="inline-flex shrink-0 text-base">
        {icon}
      </span>
      {!collapsed && (
        <span className="min-w-0 truncate font-body text-[0.9375rem] font-semibold">
          {label}
        </span>
      )}
      {badge && (
        <span
          aria-hidden="true"
          className={cn(
            "rounded-tag bg-fill-attention font-mono text-fine-print leading-[18px] text-ink",
            collapsed ? "absolute top-1 right-1 px-1" : "ml-auto px-[7px]",
          )}
        >
          {count}
        </span>
      )}
    </a>
  );
}
